import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Save, Award, FileText, ExternalLink, Plus, X } from 'lucide-react';
import { message, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import apiClient from '../services/apiConfig';
import { uploadMenuPdf } from '../services/settingService';

interface Setting {
  key: string;
  value: any;
  type: string;
  description: string;
}

interface Category {
  name: string;
  min_points: number;
  benefits?: string[];
  card_benefits?: string[];
}

interface Categories {
  classic: Category;
  premium: Category;
  silver: Category;
}

const SettingsNewPage: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [menuPdfUrl, setMenuPdfUrl] = useState<string | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [newBenefitInput, setNewBenefitInput] = useState<Record<string, string>>({ classic: '', silver: '', premium: '' });
  const [newCardBenefitInput, setNewCardBenefitInput] = useState<Record<string, string>>({ classic: '', silver: '', premium: '' });

  // Mapeo de claves a nombres en español
  const settingLabels: { [key: string]: string } = {
    'points_per_amount': 'Puntos por Monto',
    'amount_for_points': 'Monto para Puntos',
    'download_bonus_points': 'Puntos por Descargar App',
    'complete_profile_bonus_points': 'Puntos por Completar Datos',
    'share_app_bonus_points': 'Puntos por Compartir App',
    'hotel_name': 'Nombre del Hotel',
    'hotel_location': 'Ubicación del Hotel',
    'categories': 'Categorías de Huéspedes'
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/settings');
      if (response.data.success) {
        setSettings(response.data.data);
        // Extraer URL del PDF del menú si existe
        const pdfSetting = response.data.data.find((s: Setting) => s.key === 'menu_pdf_url');
        if (pdfSetting) {
          setMenuPdfUrl(pdfSetting.value);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      message.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuPdfUpload = async (file: File) => {
    // Validar que sea PDF
    if (file.type !== 'application/pdf') {
      message.error('Solo se permiten archivos PDF');
      return;
    }
    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      message.error('El archivo no debe superar los 10MB');
      return;
    }

    setUploadingPdf(true);
    try {
      const url = await uploadMenuPdf(file);
      setMenuPdfUrl(url);
      message.success('PDF del menú actualizado correctamente');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      message.error('Error al subir el PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/settings', { settings });
      message.success('Configuración guardada exitosamente');
    } catch (error: any) {
      message.error('Error al guardar: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: string, value: any) => {
    setSettings(settings.map(s => s.key === key ? { ...s, value } : s));
  };

  const updateCategory = (categoryKey: keyof Categories, field: keyof Category, value: any) => {
    const categoriesSetting = settings.find(s => s.key === 'categories');
    if (!categoriesSetting) return;

    const categories = { ...categoriesSetting.value };
    categories[categoryKey][field] = field === 'min_points' ? parseInt(value) || 0 : value;

    updateSetting('categories', categories);
  };

  const addBenefit = (categoryKey: keyof Categories, field: 'benefits' | 'card_benefits', text: string) => {
    if (!text.trim()) return;
    const categoriesSetting = settings.find(s => s.key === 'categories');
    if (!categoriesSetting) return;

    const categories = JSON.parse(JSON.stringify(categoriesSetting.value));
    const current = categories[categoryKey][field] || [];

    if (field === 'card_benefits' && current.length >= 3) {
      message.warning('Máximo 3 beneficios en tarjeta');
      return;
    }

    categories[categoryKey][field] = [...current, text.trim()];
    updateSetting('categories', categories);

    if (field === 'benefits') {
      setNewBenefitInput(prev => ({ ...prev, [categoryKey]: '' }));
    } else {
      setNewCardBenefitInput(prev => ({ ...prev, [categoryKey]: '' }));
    }
  };

  const removeBenefit = (categoryKey: keyof Categories, field: 'benefits' | 'card_benefits', index: number) => {
    const categoriesSetting = settings.find(s => s.key === 'categories');
    if (!categoriesSetting) return;

    const categories = JSON.parse(JSON.stringify(categoriesSetting.value));
    const current = [...(categories[categoryKey][field] || [])];
    current.splice(index, 1);
    categories[categoryKey][field] = current;
    updateSetting('categories', categories);
  };

  const renderBenefitsEditor = (categoryKey: keyof Categories) => {
    const categoriesValue = settings.find(s => s.key === 'categories')?.value;
    if (!categoriesValue) return null;
    const category = categoriesValue[categoryKey];

    return (
      <>
        {/* Beneficios completos */}
        <div className="mt-3">
          <label className="text-xs text-slate-600 block mb-1 font-semibold">Beneficios completos</label>
          <div className="space-y-1 mb-2">
            {(category.benefits || []).map((benefit: string, idx: number) => (
              <div key={idx} className="flex items-center gap-1 bg-white rounded px-2 py-1 border text-sm">
                <span className="flex-1">{benefit}</span>
                <button
                  onClick={() => removeBenefit(categoryKey, 'benefits', idx)}
                  className="text-red-400 hover:text-red-600 flex-shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-1">
            <Input
              placeholder="Nuevo beneficio..."
              value={newBenefitInput[categoryKey]}
              onChange={(e) => setNewBenefitInput(prev => ({ ...prev, [categoryKey]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addBenefit(categoryKey, 'benefits', newBenefitInput[categoryKey]);
                }
              }}
            />
            <button
              onClick={() => addBenefit(categoryKey, 'benefits', newBenefitInput[categoryKey])}
              className="px-2 py-1 bg-slate-200 hover:bg-slate-300 rounded text-slate-700 flex-shrink-0"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Beneficios en tarjeta (max 3) */}
        <div className="mt-3">
          <label className="text-xs text-slate-600 block mb-1 font-semibold">
            Beneficios en tarjeta ({(category.card_benefits || []).length}/3)
          </label>
          <div className="space-y-1 mb-2">
            {(category.card_benefits || []).map((benefit: string, idx: number) => (
              <div key={idx} className="flex items-center gap-1 bg-white rounded px-2 py-1 border text-sm">
                <span className="flex-1">{benefit}</span>
                <button
                  onClick={() => removeBenefit(categoryKey, 'card_benefits', idx)}
                  className="text-red-400 hover:text-red-600 flex-shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
          {(category.card_benefits || []).length < 3 && (
            <div className="flex gap-1">
              <Input
                placeholder="Beneficio corto..."
                value={newCardBenefitInput[categoryKey]}
                onChange={(e) => setNewCardBenefitInput(prev => ({ ...prev, [categoryKey]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addBenefit(categoryKey, 'card_benefits', newCardBenefitInput[categoryKey]);
                  }
                }}
              />
              <button
                onClick={() => addBenefit(categoryKey, 'card_benefits', newCardBenefitInput[categoryKey])}
                className="px-2 py-1 bg-slate-200 hover:bg-slate-300 rounded text-slate-700 flex-shrink-0"
              >
                <Plus size={16} />
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-600">Cargando configuración...</div>;
  }

  const categoriesSetting = settings.find(s => s.key === 'categories');
  const regularSettings = settings.filter(s => s.key !== 'categories');

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Configuración del Sistema</h1>
          <p className="text-sm text-slate-600 mt-1">Gestiona las configuraciones globales del hotel</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save size={18} className="mr-2" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      {/* Configuraciones Generales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {regularSettings.map((setting) => (
          <Card key={setting.key}>
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-slate-900">
                  {settingLabels[setting.key] || setting.key.replace(/_/g, ' ')}
                </h3>
                <p className="text-sm text-slate-600">{setting.description}</p>
              </div>

              <Input
                type={setting.type === 'number' ? 'number' : 'text'}
                value={setting.value}
                onChange={(e) => updateSetting(setting.key, e.target.value)}
              />
            </div>
          </Card>
        ))}
      </div>

      {/* Categorías de Huéspedes */}
      {categoriesSetting && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Award size={24} className="text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  {settingLabels['categories']}
                </h2>
                <p className="text-sm text-slate-600">{categoriesSetting.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Classic */}
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  <h3 className="font-semibold text-slate-900">Classic</h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Nombre</label>
                    <Input
                      value={categoriesSetting.value.classic.name}
                      onChange={(e) => updateCategory('classic', 'name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Puntos Mínimos</label>
                    <Input
                      type="number"
                      value={categoriesSetting.value.classic.min_points}
                      onChange={(e) => updateCategory('classic', 'min_points', e.target.value)}
                    />
                  </div>
                  {renderBenefitsEditor('classic')}
                </div>
              </div>

              {/* Silver */}
              <div className="border border-slate-300 bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-slate-500 rounded-full"></div>
                  <h3 className="font-semibold text-slate-900">Silver</h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Nombre</label>
                    <Input
                      value={categoriesSetting.value.silver.name}
                      onChange={(e) => updateCategory('silver', 'name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Puntos Mínimos</label>
                    <Input
                      type="number"
                      value={categoriesSetting.value.silver.min_points}
                      onChange={(e) => updateCategory('silver', 'min_points', e.target.value)}
                    />
                  </div>
                  {renderBenefitsEditor('silver')}
                </div>
              </div>

              {/* Premium */}
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <h3 className="font-semibold text-slate-900">Premium</h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Nombre</label>
                    <Input
                      value={categoriesSetting.value.premium.name}
                      onChange={(e) => updateCategory('premium', 'name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 block mb-1">Puntos Mínimos</label>
                    <Input
                      type="number"
                      value={categoriesSetting.value.premium.min_points}
                      onChange={(e) => updateCategory('premium', 'min_points', e.target.value)}
                    />
                  </div>
                  {renderBenefitsEditor('premium')}
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg mt-4">
              <p className="text-sm text-slate-700">
                <strong>Nota:</strong> Los huéspedes se clasifican automáticamente según sus puntos acumulados.
                Los puntos mínimos definen el umbral para cada categoría.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Menú del Restaurante (PDF) */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={24} className="text-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Menú del Restaurante</h2>
              <p className="text-sm text-slate-600">Sube el PDF del menú que se mostrará en la aplicación móvil</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {menuPdfUrl ? (
              <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={20} className="text-green-600" />
                    <span className="text-green-800 font-medium">PDF configurado</span>
                  </div>
                  <a
                    href={menuPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Ver menú actual <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-amber-600" />
                  <span className="text-amber-800">No hay PDF configurado</span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <Upload
              accept=".pdf"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                handleMenuPdfUpload(file);
                return false;
              }}
            >
              <Button disabled={uploadingPdf}>
                <UploadOutlined className="mr-2" />
                {uploadingPdf ? 'Subiendo...' : 'Subir nuevo PDF'}
              </Button>
            </Upload>
          </div>

          <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg">
            <p className="text-sm text-slate-700">
              <strong>Nota:</strong> El PDF debe tener un tamaño máximo de 10MB.
              Al subir un nuevo archivo, el anterior será reemplazado automáticamente.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SettingsNewPage;
